import { Controller } from "@hotwired/stimulus";
import {
  errorDetail,
  formatDate,
  getAllJSON,
  getJSON,
  listResults,
  sendJSON,
} from "../api.js";

// Reklamacje: lista + zgłoszenie (klient) + rozstrzyganie (personel, staffValue).
// 0-PII: operujemy na kodach pozycji (order_item_code), nigdy na danych leada.
export default class extends Controller {
  static targets = [
    "rows",
    "status",
    "itemSelect",
    "reason",
    "formError",
    "modal",
    "modalTitle",
    "modalBody",
    "modalError",
    "confirmBtn",
    "candidates",
  ];
  static values = {
    url: String, // /api/complaints/ (może nieść ?status= — filtr server-side)
    ordersUrl: String, // /api/orders/ (pozycje do formularza)
    staff: Boolean,
    management: Boolean, // panel personelu: rozstrzyganie przez modal potwierdzenia + wybór zamiennika
  };

  static RESOLUTION_LABEL = {
    replaced: "Wymieniono",
    credited: "Zwrot środków (kredyt)",
    rejected: "Odrzucona",
  };

  connect() {
    this.load();
    this.#loadItems();
  }

  async load() {
    let data;
    try {
      data = await getJSON(this.urlValue);
    } catch {
      this.#status("Nie udało się załadować reklamacji. Odśwież stronę.");
      return;
    }
    const complaints = listResults(data);
    this.rowsTarget.textContent = "";
    if (!complaints.length) {
      this.#status(
        this.managementValue ? "Brak otwartych reklamacji." : "Brak zgłoszeń.",
      );
      return;
    }
    this.#status(null);
    const template = document.getElementById("complaint-row-template");
    for (const complaint of complaints) {
      const row = template.content.firstElementChild.cloneNode(true);
      row.querySelector('[data-field="code"]').textContent =
        complaint.order_item_code;
      row.querySelector('[data-field="reason"]').textContent = complaint.reason;
      row.querySelector('[data-field="created"]').textContent = formatDate(
        complaint.created,
      );

      const open = complaint.status === "open";
      const badge = row.querySelector('[data-field="badge"]');
      badge.className = `ls-badge ${open ? "ls-badge--warm" : "ls-badge--sent"}`;
      badge.textContent = open ? "otwarta" : "rozstrzygnięta";

      const resolutionCell = row.querySelector('[data-field="resolution"]');
      if (!open) {
        resolutionCell.textContent =
          this.constructor.RESOLUTION_LABEL[complaint.resolution] ??
          complaint.resolution;
      } else if (this.staffValue) {
        for (const [resolution, label] of Object.entries(
          this.constructor.RESOLUTION_LABEL,
        )) {
          const btn = document.createElement("button");
          btn.type = "button";
          btn.className = "ls-btn ls-btn--ghost ls-btn--sm";
          btn.style.cssText =
            "font-size:12px;padding:6px 10px;margin-right:6px;";
          btn.textContent = label;
          // Panel personelu: modal potwierdzenia (rozstrzygnięcie nieodwracalne). Strona klienta
          // (gdyby staff ją otworzył) zostaje przy bezpośrednim resolve — brak modala w DOM.
          btn.setAttribute(
            "data-action",
            this.managementValue
              ? "complaints#openResolve"
              : "complaints#resolve",
          );
          btn.setAttribute("data-complaints-id-param", complaint.id);
          btn.setAttribute("data-complaints-resolution-param", resolution);
          btn.setAttribute(
            "data-complaints-code-param",
            complaint.order_item_code,
          );
          resolutionCell.appendChild(btn);
        }
      } else {
        resolutionCell.textContent = "— w rozpatrywaniu";
      }
      this.rowsTarget.appendChild(row);
    }
  }

  async create() {
    const orderItem = this.itemSelectTarget.value;
    const reason = this.reasonTarget.value.trim();
    if (!orderItem || !reason) {
      this.#formError("Wybierz pozycję i podaj powód reklamacji.");
      return;
    }
    const res = await sendJSON("POST", this.urlValue, {
      order_item: Number(orderItem),
      reason,
    });
    if (!res.ok) {
      this.#formError(
        errorDetail(res.data, "Nie udało się zgłosić reklamacji."),
      );
      return;
    }
    this.#formError(null);
    this.reasonTarget.value = "";
    await this.load();
  }

  async resolve(event) {
    const res = await sendJSON(
      "POST",
      `${this.#base()}${event.params.id}/resolve/`,
      { resolution: event.params.resolution },
    );
    if (!res.ok) {
      this.#formError(
        errorDetail(res.data, "Nie udało się rozstrzygnąć reklamacji."),
      );
      return;
    }
    await this.load();
  }

  // Panel personelu: otwórz modal potwierdzenia. Dla 'replaced' pobierz kandydatów (0-PII) do wyboru.
  async openResolve(event) {
    this.pending = { id: event.params.id, resolution: event.params.resolution };
    const label =
      this.constructor.RESOLUTION_LABEL[this.pending.resolution] ??
      this.pending.resolution;
    if (this.hasModalTitleTarget)
      this.modalTitleTarget.textContent = `Rozstrzygnięcie: ${label}`;
    if (this.hasModalErrorTarget) this.modalErrorTarget.hidden = true;
    if (this.hasCandidatesTarget) this.candidatesTarget.textContent = "";
    this.selectedCandidate = null;

    if (this.pending.resolution === "replaced") {
      // Zamiennik wymagany — potwierdzenie zablokowane do czasu wyboru.
      if (this.hasConfirmBtnTarget) this.confirmBtnTarget.disabled = true;
      if (this.hasModalBodyTarget) {
        this.modalBodyTarget.textContent = `Wybierz listing-zamiennik dla pozycji ${event.params.code}. Rozstrzygnięcie jest nieodwracalne.`;
      }
      await this.#loadCandidates(this.pending.id);
    } else {
      if (this.hasConfirmBtnTarget) this.confirmBtnTarget.disabled = false;
      if (this.hasModalBodyTarget) {
        this.modalBodyTarget.textContent = `Potwierdź rozstrzygnięcie „${label}" dla pozycji ${event.params.code}. Operacja jest nieodwracalna.`;
      }
    }
    if (this.hasModalTarget) this.modalTarget.hidden = false;
  }

  selectCandidate(event) {
    this.selectedCandidate = event.currentTarget.value;
    if (this.hasConfirmBtnTarget) this.confirmBtnTarget.disabled = false;
  }

  async confirmResolve() {
    if (!this.pending) return;
    const body = { resolution: this.pending.resolution };
    if (this.pending.resolution === "replaced") {
      if (!this.selectedCandidate) return;
      body.replacement_listing = Number(this.selectedCandidate);
    }
    if (this.hasConfirmBtnTarget) this.confirmBtnTarget.disabled = true;
    const res = await sendJSON(
      "POST",
      `${this.#base()}${this.pending.id}/resolve/`,
      body,
    );
    if (!res.ok) {
      // 409 = już rozstrzygnięta / konflikt zamiennika — pokaż w modalu, odśwież listę pod spodem.
      this.#modalError(
        errorDetail(res.data, "Nie udało się rozstrzygnąć reklamacji."),
      );
      if (this.hasConfirmBtnTarget) this.confirmBtnTarget.disabled = false;
      await this.load();
      return;
    }
    this.closeModal();
    await this.load();
  }

  closeModal() {
    this.pending = null;
    this.selectedCandidate = null;
    if (this.hasModalTarget) this.modalTarget.hidden = true;
  }

  // Klik wewnątrz modala nie może zamykać overlaya (który nasłuchuje click->closeModal).
  stop(event) {
    event.stopPropagation();
  }

  // --- prywatne ---

  // Bazowy URL akcji na zasobie — bez ewentualnego ?status= z urlValue (filtr dotyczy tylko listy).
  #base() {
    return this.urlValue.split("?")[0];
  }

  async #loadCandidates(complaintId) {
    let candidates;
    try {
      candidates = await getJSON(
        `${this.#base()}${complaintId}/replacement-candidates/`,
      );
    } catch {
      this.#modalError("Nie udało się pobrać kandydatów na zamiennika.");
      return;
    }
    const list = listResults(candidates);
    if (!this.hasCandidatesTarget) return;
    if (!list.length) {
      this.candidatesTarget.textContent =
        "Brak dostępnych zamienników w tej podkategorii.";
      return;
    }
    for (const candidate of list) {
      const option = document.createElement("label");
      option.className = "ls-candidate";
      option.style.cssText =
        "display:flex;gap:10px;align-items:center;padding:8px 0;font-size:13.5px;";
      const radio = document.createElement("input");
      radio.type = "radio";
      radio.name = "replacement";
      radio.value = candidate.id;
      radio.setAttribute("data-action", "complaints#selectCandidate");
      const text = document.createElement("span");
      text.textContent = `${candidate.code} · ${candidate.subcategory} · ${candidate.voivodeship}/${candidate.county} · ${candidate.lead_type}`;
      option.append(radio, text);
      this.candidatesTarget.appendChild(option);
    }
  }

  #modalError(message) {
    if (!this.hasModalErrorTarget) return;
    this.modalErrorTarget.hidden = false;
    this.modalErrorTarget.textContent = message;
  }

  async #loadItems() {
    if (!this.hasItemSelectTarget) return;
    let orders;
    try {
      orders = await getAllJSON(this.ordersUrlValue); // wszystkie strony — pozycja może być w starszym zamówieniu
    } catch {
      return;
    }
    for (const order of orders) {
      if (order.status !== "paid" && order.status !== "delivered") continue;
      for (const item of order.items ?? []) {
        const option = document.createElement("option");
        option.value = item.id;
        option.textContent = `${item.code} — ${item.category_path}`;
        this.itemSelectTarget.appendChild(option);
      }
    }
  }

  #formError(message) {
    if (!this.hasFormErrorTarget) return;
    this.formErrorTarget.hidden = message === null;
    if (message !== null) this.formErrorTarget.textContent = message;
  }

  #status(message) {
    if (!this.hasStatusTarget) return;
    this.statusTarget.hidden = message === null;
    if (message !== null) this.statusTarget.textContent = message;
  }
}
